import { useState, useEffect } from "react";
import { useAuth } from "wasp/client/auth";
import { useQuery } from "wasp/client/operations";
import {
  getPlatformOptionalFieldVisibility,
  updatePlatformOptionalFieldVisibility,
} from "wasp/client/operations";
import { Link, useParams } from "react-router-dom";
import DefaultLayout from "../../admin/layout/DefaultLayout";
import LoadingSpinner from "../../admin/layout/LoadingSpinner";
import { Switch } from "../../components/ui/switch";
import { EnhancedActionButton } from "../../components/ui/enhanced-page-layout";
import { Save, ArrowLeft } from "lucide-react";
import { SECTION_LABELS } from "../location-types/constants";
import {
  SECTION_OPTIONAL_FIELDS,
  SECTIONS_WITH_OPTIONAL_FIELDS,
  SECTION_FIELD_DEFINITIONS,
} from "./optionalFieldConstants";
import { toast } from "../../hooks/use-toast";

export default function AdminSectionFieldVisibilityPage() {
  const { data: user } = useAuth();
  const { sectionKey } = useParams<{ sectionKey: string }>();

  const fieldDefs = sectionKey
    ? SECTION_FIELD_DEFINITIONS[sectionKey]
    : undefined;
  const toggleableKeys = sectionKey
    ? SECTION_OPTIONAL_FIELDS[sectionKey] ?? []
    : [];
  const isValidSection =
    sectionKey != null &&
    SECTIONS_WITH_OPTIONAL_FIELDS.includes(sectionKey) &&
    (fieldDefs?.length ?? 0) > 0;

  const { data, isLoading, error, refetch } = useQuery(
    getPlatformOptionalFieldVisibility,
  );
  const [localByKey, setLocalByKey] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);

  const byKey =
    (data as { byKey?: Record<string, boolean> } | undefined)?.byKey ?? {};

  useEffect(() => {
    if (toggleableKeys.length === 0) return;
    const initial: Record<string, boolean> = {};
    for (const key of toggleableKeys) {
      initial[key] = byKey[key] !== false;
    }
    setLocalByKey(initial);
  }, [byKey, toggleableKeys]);

  const handleToggle = (fieldKey: string, visible: boolean) => {
    setLocalByKey((prev) => ({ ...prev, [fieldKey]: visible }));
  };

  const handleSave = async () => {
    if (toggleableKeys.length === 0) return;
    setSaving(true);
    try {
      const updates: Record<string, boolean> = {};
      for (const key of toggleableKeys) {
        updates[key] = localByKey[key] !== false;
      }
      await updatePlatformOptionalFieldVisibility({ updates });
      await refetch();
      toast({
        title: "Saved",
        description: "Field visibility updated.",
      });
    } catch (e: any) {
      toast({
        title: "Error",
        description: e.message || "Failed to update field visibility",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (!user) {
    return (
      <DefaultLayout user={null as any}>
        <div className="p-4 m-4 bg-yellow-50 border border-yellow-200 rounded-md">
          <div className="text-yellow-800 font-semibold">
            Authentication Required
          </div>
          <div className="text-yellow-600 mt-1">
            Please log in to access this page.
          </div>
        </div>
      </DefaultLayout>
    );
  }

  if (!isValidSection) {
    return (
      <DefaultLayout user={user as any}>
        <div className="p-4 md:px-6">
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-md text-amber-800 dark:bg-amber-950/30 dark:border-amber-700 dark:text-amber-200">
            <p className="font-semibold">Invalid section</p>
            <p className="text-sm mt-1">
              This section has no configurable fields or the section key is not
              recognized.
            </p>
          </div>
          <Link
            to="/admin/section-visibility"
            className="inline-flex items-center gap-2 mt-4 text-sm text-primary hover:underline"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Section visibility
          </Link>
        </div>
      </DefaultLayout>
    );
  }

  const sectionLabel = SECTION_LABELS[sectionKey!] ?? sectionKey;

  return (
    <DefaultLayout user={user as any}>
      <div className="w-full">
        <div className="border-b border-border min-h-[4.5rem] flex flex-col justify-center bg-background">
          <div className="px-4 py-3">
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0 max-w-[70%]">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                  <Link
                    to="/admin/section-visibility"
                    className="hover:text-foreground"
                  >
                    Section visibility
                  </Link>
                  <span>/</span>
                  <span className="text-foreground">
                    {sectionLabel} — Fields
                  </span>
                </div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white break-words">
                  {sectionLabel} — Optional fields
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 break-words">
                  Show or hide optional fields in forms and views for this
                  section. Hidden fields are not shown in the UI but data is
                  still stored.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <EnhancedActionButton
                  onClick={handleSave}
                  icon={Save}
                  label={saving ? "Saving..." : "Save changes"}
                  disabled={saving}
                  hideLabelOnMobile
                />
              </div>
            </div>
          </div>
        </div>

        <div className="px-4 py-6 md:px-6">
          {isLoading && <LoadingSpinner />}
          {error && (
            <div className="p-4 mb-6 bg-red-50 border border-red-200 rounded-md text-red-800 dark:bg-red-950/30 dark:border-red-800 dark:text-red-200">
              {error.message || "Failed to load field visibility settings"}
            </div>
          )}

          {!isLoading && !error && fieldDefs && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground mb-2">
                Title and description are always shown and cannot be toggled for
                now. Other fields can be hidden or shown.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {fieldDefs.map((def) => (
                  <div
                    key={def.key}
                    className="flex items-center justify-between gap-3 py-2 px-3 rounded-lg bg-gray-50/50 hover:bg-gray-50 dark:bg-gray-800/50 dark:hover:bg-gray-800"
                  >
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {def.label}
                    </span>
                    {def.canToggle ? (
                      <Switch
                        checked={localByKey[def.key] !== false}
                        onCheckedChange={(checked) =>
                          handleToggle(def.key, checked)
                        }
                      />
                    ) : (
                      <span className="text-xs text-muted-foreground italic">
                        Always shown
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </DefaultLayout>
  );
}
